import React, { useCallback, useEffect, useState } from "react";
import { Search, ChevronRight, Ban, CheckCircle2 } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { adminApi } from "@/lib/admin";
import { formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";

const inr = (v) => `₹${Number(v || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;

export default function Factories() {
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [plans, setPlans] = useState([]);
  const [assigning, setAssigning] = useState(null);
  const [assignForm, setAssignForm] = useState({ plan_id: "", status: "active", days: 30 });

  const load = useCallback(() => {
    adminApi.get("/factories", { params: q ? { q } : {} }).then((r) => setItems(r.data));
  }, [q]);

  useEffect(() => {
    adminApi.get("/plans").then((r) => setPlans(r.data));
  }, []);
  useEffect(() => {
    const h = setTimeout(load, 200);
    return () => clearTimeout(h);
  }, [load]);

  const openAssign = (f) => {
    setAssigning(f);
    setAssignForm({
      plan_id: f.subscription?.plan_id || plans[0]?.id || "",
      status: f.subscription?.status || "active",
      days: 30,
    });
  };

  const saveAssign = async () => {
    try {
      await adminApi.put(`/factories/${assigning.id}/subscription`, assignForm);
      toast.success("Subscription updated");
      setAssigning(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const suspend = async (f) => {
    try {
      await adminApi.delete(`/factories/${f.id}`);
      toast.success("Factory suspended");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6">
      <div>
        <div className="sq-label">Factories</div>
        <h1 className="text-4xl font-heading font-bold mt-1 text-secondary">All factories</h1>
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={16} />
        <Input data-testid="admin-factories-search" value={q} onChange={(e) => setQ(e.target.value)}
          placeholder="Search by name, business, phone" className="h-12 pl-10" />
      </div>

      <div className="sq-card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted/60 text-muted-foreground">
            <tr>
              <Th>Factory</Th><Th>Phone</Th><Th className="text-right">Designs</Th>
              <Th className="text-right">Quoted</Th><Th>Plan</Th><Th>Status</Th><Th></Th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && (
              <tr><td colSpan={7} className="p-10 text-center text-muted-foreground">No factories yet.</td></tr>
            )}
            {items.map((f) => (
              <tr key={f.id} className="border-t border-border" data-testid="admin-factory-row">
                <td className="p-3">
                  <div className="font-semibold">{f.business_name || f.name || "—"}</div>
                  <div className="text-xs text-muted-foreground">{f.name || "no name"}</div>
                  <Link to={`/admin/factories/${f.id}`} className="text-xs text-primary hover:underline mt-1 inline-flex items-center gap-0.5">
                    View orders <ChevronRight size={12} />
                  </Link>
                </td>
                <td className="p-3">{f.phone}</td>
                <td className="p-3 text-right font-semibold">{f.designs_count}</td>
                <td className="p-3 text-right font-heading font-bold">{inr(f.quoted_value)}</td>
                <td className="p-3">{f.plan?.name || <span className="text-muted-foreground">—</span>}</td>
                <td className="p-3">
                  <StatusPill s={f.subscription?.status} />
                </td>
                <td className="p-3 whitespace-nowrap">
                  <Button size="sm" variant="outline" onClick={() => openAssign(f)} data-testid="admin-assign-plan">
                    Assign plan
                  </Button>
                  {f.subscription?.status !== "suspended" && (
                    <Button size="sm" variant="ghost" onClick={() => suspend(f)} className="ml-1 text-destructive">
                      <Ban size={14} />
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={!!assigning} onOpenChange={(v) => !v && setAssigning(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Assign plan · {assigning?.business_name || assigning?.name}</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <div className="sq-label mb-1">Plan</div>
              <Select value={assignForm.plan_id} onValueChange={(v) => setAssignForm({ ...assignForm, plan_id: v })}>
                <SelectTrigger className="h-11" data-testid="assign-plan-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {plans.map((p) => <SelectItem key={p.id} value={p.id}>{p.name} · ₹{p.price_monthly}/mo</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <div className="sq-label mb-1">Status</div>
                <Select value={assignForm.status} onValueChange={(v) => setAssignForm({ ...assignForm, status: v })}>
                  <SelectTrigger className="h-11" data-testid="assign-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="trial">Trial</SelectItem>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="suspended">Suspended</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <div className="sq-label mb-1">Duration (days)</div>
                <Input type="number" value={assignForm.days}
                  onChange={(e) => setAssignForm({ ...assignForm, days: parseInt(e.target.value || "0", 10) })}
                  className="h-11" />
              </div>
            </div>
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setAssigning(null)}>Cancel</Button>
            <Button onClick={saveAssign} data-testid="assign-save-btn"><CheckCircle2 size={14} className="mr-1" /> Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Th({ children, className = "" }) {
  return <th className={`text-left p-3 sq-label ${className}`}>{children}</th>;
}
function StatusPill({ s }) {
  if (!s) return <span className="text-muted-foreground">—</span>;
  const map = {
    active: "bg-success/15 text-success border-success/30",
    trial: "bg-warning/15 text-warning border-warning/30",
    suspended: "bg-destructive/15 text-destructive border-destructive/30",
  };
  return (
    <span className={`text-[10px] uppercase tracking-widest font-bold border rounded-full px-2 py-0.5 ${map[s] || ""}`}>
      {s}
    </span>
  );
}
